import { useState } from 'react';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';
import { LoadingStatus } from '@/shared/ui/spinner';

import {
  downloadFieldAdminDocument,
  useFieldAdminRevisions,
  useFieldAdminSourceManifest,
  useFieldAdminSourcePage,
} from '../api/field-admin-client';
import { fieldErrorMessage } from '../field-view';

import { FIELD_LABEL } from './field-types';

import type { FieldAdminDetail, FieldDocument } from '@mio/shared';

export function FieldAdminEvidence({ detail }: { detail: FieldAdminDetail }) {
  const [prId, setPrId] = useState(''),
    [revision, setRevision] = useState(''),
    [offset, setOffset] = useState(0),
    [path, setPath] = useState(''),
    [page, setPage] = useState(0);
  const [downloadError, setDownloadError] = useState<string | null>(null),
    [downloading, setDownloading] = useState(false);
  const revisions = useFieldAdminRevisions(detail.learnerId, prId, offset),
    manifest = useFieldAdminSourceManifest(detail.learnerId, prId, revision),
    source = useFieldAdminSourcePage(detail.learnerId, prId, revision, path, page);
  const download = (document: FieldDocument, format: FieldDocument['formats'][number]) => {
    setDownloadError(null);
    setDownloading(true);
    void downloadFieldAdminDocument(detail.learnerId, document, format)
      .catch((error) => setDownloadError(fieldErrorMessage(error)))
      .finally(() => setDownloading(false));
  };
  return (
    <section
      className="border-line min-w-0 space-y-4 border-t pt-5 [overflow-wrap:anywhere]"
      aria-label="Immutable evidence inspector"
    >
      <h3 className="text-base font-semibold">Inspect stored evidence</h3>
      <p className="text-fg-muted max-w-3xl text-sm leading-6">
        Source integrity and workspace capture receipts establish which bytes were stored. They do
        not prove execution, passing tests, reviewer approval, or deployment. Assess those claims
        using trusted runs for the same revision.
      </p>
      <label className={FIELD_LABEL} htmlFor="evidence-pr">
        Pull request
      </label>
      <SelectField
        id="evidence-pr"
        value={prId}
        onValueChange={(nextValue) => {
          setPrId(nextValue);
          setRevision(detail.snapshot.prs.find((p) => p.id === nextValue)?.revision ?? '');
          setOffset(0);
          setPath('');
          setPage(0);
        }}
        options={[
          { value: '', label: 'Select a pull request to inspect' },
          ...(detail.snapshot.prs.map((pr) => ({ value: pr.id, label: pr.title })) ?? []),
        ]}
      />
      {prId ? (
        <>
          <label className={FIELD_LABEL} htmlFor="evidence-revision">
            Immutable revision
          </label>
          <SelectField
            id="evidence-revision"
            value={revision}
            onValueChange={(nextValue) => {
              setRevision(nextValue);
              setPath('');
              setPage(0);
            }}
            options={[
              ...(revision && !revisions.data?.revisions.some((r) => r.revision === revision)
                ? [{ value: revision, label: <span title={revision}>{revision.slice(0, 8)}</span> }]
                : []),
              ...(revisions.data?.revisions.map((r) => ({
                value: r.revision,
                label: (
                  <>
                    <span title={r.revision}>{r.revision.slice(0, 8)}</span> · {r.fileCount}{' '}
                    {r.fileCount === 1 ? 'file' : 'files'}
                    {r.revision === revisions.data.currentRevision ? ' · current' : ''}
                  </>
                ),
              })) ?? []),
            ]}
          />
          {offset > 0 ||
          (revisions.data?.nextOffset !== null && revisions.data?.nextOffset !== undefined) ? (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - 50))}
              >
                Newer revisions
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={revisions.data?.nextOffset == null}
                onClick={() => setOffset(revisions.data?.nextOffset ?? offset)}
              >
                Older revisions
              </Button>
            </div>
          ) : null}
          {revisions.error || manifest.error ? (
            <Alert>{fieldErrorMessage(revisions.error ?? manifest.error)}</Alert>
          ) : null}
          {manifest.isFetching ? <LoadingStatus label="Reading the stored revision…" /> : null}
          {manifest.data ? (
            <>
              <p className="text-fg-muted break-all text-xs">
                {manifest.data.provenance === 'workspace_capture'
                  ? 'Server workspace capture'
                  : manifest.data.provenance === 'learner_upload'
                    ? 'Learner-uploaded snapshot'
                    : 'Stored snapshot; original capture provenance unavailable'}{' '}
                · {manifest.data.totalBytes} source bytes ·{' '}
                {manifest.data.revision === manifest.data.currentRevision
                  ? 'Current pull request revision'
                  : 'Historical pull request revision'}
              </p>
              {manifest.data.capture ? (
                <details className="text-sm">
                  <summary className="cursor-pointer font-medium">Capture receipt details</summary>
                  <dl className="text-fg-muted mt-3 grid gap-2 text-xs">
                    <div>
                      <dt className="font-semibold">Captured</dt>
                      <dd>{new Date(manifest.data.capture.capturedAt).toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold">Receipt ID</dt>
                      <dd className="break-all font-mono">{manifest.data.capture.operationId}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold">Generation</dt>
                      <dd>{manifest.data.capture.context.generation}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold">Artifact fingerprint</dt>
                      <dd className="break-all font-mono">{manifest.data.capture.artifactHash}</dd>
                    </div>
                  </dl>
                </details>
              ) : null}
              <label className={FIELD_LABEL} htmlFor="evidence-file">
                Source file
              </label>
              <SelectField
                id="evidence-file"
                value={path}
                onValueChange={(nextValue) => {
                  setPath(nextValue);
                  setPage(0);
                }}
                options={[
                  { value: '', label: 'Select a captured file' },
                  ...(manifest.data.files.map((file) => ({
                    value: file.path,
                    label: (
                      <>
                        {file.path} · {file.byteSize} bytes · {file.pageCount} pages
                      </>
                    ),
                  })) ?? []),
                ]}
              />
            </>
          ) : null}
          {source.error ? <Alert>{fieldErrorMessage(source.error)}</Alert> : null}
          {path && source.isFetching ? <LoadingStatus label="Loading source page…" /> : null}
          {path && source.data ? (
            <div className="space-y-3">
              <p className="text-sm font-medium [overflow-wrap:anywhere]">{source.data.path}</p>
              <details className="text-fg-muted text-xs">
                <summary className="cursor-pointer">Source fingerprint (SHA-256)</summary>
                <code className="mt-2 block break-all">{source.data.sha256}</code>
              </details>
              <pre
                tabIndex={0}
                role="region"
                aria-label="Captured source"
                className="bg-surface max-h-[32rem] max-w-full overflow-auto rounded-md p-4 text-xs leading-6"
              >
                <code>{source.data.content}</code>
              </pre>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous source page
                </Button>
                <span className="text-xs">
                  Page {source.data.page + 1} of {source.data.pageCount}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page + 1 >= source.data.pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next source page
                </Button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
      <h4 className="text-sm font-medium">Document versions</h4>
      {downloadError ? <Alert>{downloadError}</Alert> : null}
      {downloading ? <LoadingStatus label="Preparing document download…" /> : null}
      {detail.snapshot.documents.length === 0 ? (
        <p className="text-fg-muted text-xs">No document versions have been recorded.</p>
      ) : null}
      <div className="space-y-3">
        {detail.snapshot.documents.map((document) => (
          <article key={document.id} className="border-line min-w-0 space-y-2 border-t py-3">
            <p className="text-sm">
              {document.title} · Version {document.version} ·{' '}
              {document.status.charAt(0).toUpperCase() + document.status.slice(1)}
            </p>
            <p className="text-fg-muted break-all text-xs">
              By{' '}
              {document.author === 'learner'
                ? 'Learner'
                : (detail.snapshot.actors.find((actor) => actor.id === document.author)?.name ??
                  document.author)}
            </p>
            <div className="flex flex-wrap gap-2">
              {document.formats.map((format) => (
                <Button
                  key={format}
                  size="sm"
                  variant="secondary"
                  disabled={downloading}
                  aria-label={`Download ${format === 'markdown' ? 'Markdown' : format.toUpperCase()}: ${document.title}, version ${document.version}`}
                  onClick={() => download(document, format)}
                >
                  Download {format === 'markdown' ? 'Markdown' : format.toUpperCase()}
                </Button>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
